import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { getShipTz } from '../lib/shipTime'
import { CONTINGENCY_CATEGORY, sortPlans, stepCount, elapsedMs, fmtElapsed } from '../lib/contingency'
import { runFor, startEmergency, subscribeEmergency } from '../lib/emergencyStore'
import { actorName } from '../lib/fleetCore'

// "Emergency" quick access from anywhere, at most 2 taps: the red Emergency button (sidebar, page header, AI panel) opens this
// sheet, a tap on a plan starts its emergency run on the Checklists page. The plans are read once per vessel and kept on the
// device, so the sheet works offline. Shown to every login with Checklists View (the module in the plan).
const RED = '#C62828'
const cacheKey = id => 'hc_contingency_plans:' + id
export const openEmergency = () => { try { window.dispatchEvent(new CustomEvent('hc-emergency-open')) } catch (e) {} }
export const emergencyAllowed = (can, hasFeature) => (!can || can('checklists', 'read')) && (!hasFeature || hasFeature('checklists'))
export const whoOf = config => actorName(config, config?.accountType === 'crew' ? (config?.email || 'Crew') : 'Captain')

// the red button (look and states: .hc-emergency-btn in src/styles/global.css). place = where it is:
//   sidebar  shown only while the sidebar is visible (desktop, >= 1024 px); compact = icon only (collapsed rail)
//   header   shown only when the sidebar is hidden (phone / iPad portrait, < 1024 px); icon + "SOS" under 400 px
//   panel    the AI panel (always)
export function EmergencyButton({ compact, place = 'panel', style }) {
  const { can, hasFeature } = useAuth()
  if (!emergencyAllowed(can, hasFeature)) return null
  return (
    <button type="button" className={'hc-emergency-btn hc-em-' + place + (compact ? ' hc-em-compact' : '')} onClick={openEmergency} title="Emergency - start a Contingency Plan" aria-label="Emergency - start a Contingency Plan" style={style}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
      {!compact && <span className="hc-em-long" aria-hidden="true">EMERGENCY</span>}
      {!compact && <span className="hc-em-short" aria-hidden="true">SOS</span>}
    </button>
  )
}

export default function EmergencyLauncher({ onNavigate }) {
  const { config, user, can, hasFeature } = useAuth()
  const [open, setOpen] = useState(false)
  const [plans, setPlans] = useState(() => { try { return config?.id ? JSON.parse(localStorage.getItem(cacheKey(config.id)) || '[]') : [] } catch (e) { return [] } })
  const [err, setErr] = useState('')
  const [, tick] = useState(0)
  const allowed = emergencyAllowed(can, hasFeature)
  useEffect(() => subscribeEmergency(() => tick(n => n + 1)), [])
  useEffect(() => { const f = () => setOpen(true); window.addEventListener('hc-emergency-open', f); return () => window.removeEventListener('hc-emergency-open', f) }, [])
  useEffect(() => { if (!open) return; const k = e => { if (e.key === 'Escape') setOpen(false) }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k) }, [open])
  // read the plans (with their steps, so a run can start offline) when the vessel is known and each time the sheet opens
  useEffect(() => {
    if (!config?.id || !allowed) return
    let live = true
    supabase.from('checklists').select('id,title,description,category,checklist_items(id,title,step_order,is_header,is_note,is_attendance,is_signature,is_drill_type)')
      .eq('client_id', config.id).eq('category', CONTINGENCY_CATEGORY)
      .then(({ data, error }) => {
        if (!live || error || !Array.isArray(data)) return
        const list = sortPlans(data.map(c => ({ ...c, checklist_items: [...(c.checklist_items || [])].sort((a, b) => a.step_order - b.step_order) })))
        setPlans(list); try { localStorage.setItem(cacheKey(config.id), JSON.stringify(list)) } catch (e) {}
      })
    return () => { live = false }
  }, [config?.id, open, allowed]) // eslint-disable-line
  if (!open || !allowed) return null

  const running = runFor(config?.id)
  const go = () => { setOpen(false); if (onNavigate) onNavigate('checklists'); try { window.dispatchEvent(new CustomEvent('hc-emergency-show')) } catch (e) {} }
  const start = p => {
    setErr('')
    if (running && running.run.checklistId !== p.id && !window.confirm('"' + running.run.title + '" is still running' + (running.run.mode === 'drill' ? ' (a drill)' : '') + '. Start "' + p.title + '" instead? The open run is discarded - nothing of it is saved.')) return
    try { startEmergency({ checklist: p, who: whoOf(config), uid: user && user.id, vesselId: config.id, tz: getShipTz() || config.timezone || null }); go() } catch (e) { setErr(e.message) }
  }
  return (
    <div role="dialog" aria-modal="true" aria-label="Emergency - start a Contingency Plan" onClick={() => setOpen(false)}
      style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 560, background: 'var(--bg, #0b1220)', color: 'var(--text)', borderRadius: 16, border: '2px solid ' + RED, boxShadow: '0 20px 50px rgba(0,0,0,.4)', overflow: 'hidden' }}>
        <div style={{ background: RED, color: '#fff', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{'⚠'} Emergency</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Raise the alarm / call for help per your plan - then start the plan.</div>
          </div>
          <button onClick={() => setOpen(false)} aria-label="Close" style={{ minWidth: 44, minHeight: 44, borderRadius: 10, border: '1px solid rgba(255,255,255,.5)', background: 'transparent', color: '#fff', fontSize: 20, cursor: 'pointer' }}>{'×'}</button>
        </div>
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {running && (
            <button onClick={go} style={{ minHeight: 64, borderRadius: 12, border: 'none', background: RED, color: '#fff', fontSize: 17, fontWeight: 800, cursor: 'pointer', textAlign: 'left', padding: '0 16px', fontFamily: 'inherit' }}>
              {'▶'} Back to the run: {running.run.title} · {fmtElapsed(elapsedMs(running.run))}
            </button>
          )}
          {err && <p role="alert" style={{ color: RED, margin: 0 }}>{err}</p>}
          {!plans.length && <p style={{ color: 'var(--muted)', fontSize: 14 }}>No Contingency Plans adopted on this vessel yet (Checklists {'›'} Contingency Plans {'›'} Hey Captain library). Follow the vessel's emergency procedures.</p>}
          {plans.map(p => (
            <button key={p.id} onClick={() => start(p)} style={{ minHeight: 56, borderRadius: 12, border: '1.5px solid ' + RED, background: 'transparent', color: 'var(--text)', fontSize: 17, fontWeight: 700, cursor: 'pointer', textAlign: 'left', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'inherit' }}>
              <span aria-hidden="true" style={{ color: RED, fontSize: 18 }}>{'⚠'}</span>
              <span style={{ flex: 1 }}>{p.title}</span>
              <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600, whiteSpace: 'nowrap' }}>{stepCount(p)} steps</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
